import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  List, 
  ListItem,
  Divider
} from '@mui/material';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin,
  MapPin
} from 'lucide-react';

const SIDEBAR_WIDTH = 240; // Match the drawerWidth from Layout.js

const Footer = () => {
  const location = useLocation();
  
  // Check if current route is a dashboard route
  const isDashboardRoute = location.pathname.startsWith('/dashboard');
  
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#1a1a1a',
        color: '#d1d5db',
        mt: 'auto',
        // Apply margin-left on desktop for dashboard routes to account for sidebar
        marginLeft: {
          xs: 0, // No margin on mobile (sidebar is temporary/drawer)
          sm: isDashboardRoute ? `${SIDEBAR_WIDTH}px` : 0, // Margin on desktop for dashboard routes
        },
        width: {
          xs: '100%', // Full width on mobile
          sm: isDashboardRoute ? `calc(100% - ${SIDEBAR_WIDTH}px)` : '100%', // Account for sidebar on desktop
        },
      }}
    >
      {/* Main Footer Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {/* Brand Column */}
          <Grid item xs={12} sm={6} md={3}>
            <Link 
              to="/" 
              style={{ 
                textDecoration: 'none',
                display: 'inline-block'
              }}
            >
              <Typography 
                variant="h5" 
                component="h3" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'white',
                  mb: 2,
                  '&:hover': {
                    color: '#d1d5db',
                    transition: 'color 0.2s'
                  }
                }}
              >
                Pelican Beaks
              </Typography>
            </Link>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#9ca3af',
                mb: 3,
                lineHeight: 1.7
              }}
            >
              Your trusted companion for creating unforgettable travel experiences. 
              Plan, explore, and discover the world with confidence.
            </Typography>
            {/* Social Icons */}
            {/* Commented out for now
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Box
                component="a"
                href="#"
                sx={{
                  color: '#9ca3af',
                  '&:hover': { color: 'white' },
                  transition: 'color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                }}
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </Box>
              <Box
                component="a"
                href="#"
                sx={{
                  color: '#9ca3af',
                  '&:hover': { color: 'white' },
                  transition: 'color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                }}
                aria-label="Twitter"
              >
                <Twitter size={20} />
              </Box>
              <Box
                component="a"
                href="#"
                sx={{
                  color: '#9ca3af',
                  '&:hover': { color: 'white' },
                  transition: 'color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                }}
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </Box>
              <Box
                component="a"
                href="#"
                sx={{
                  color: '#9ca3af',
                  '&:hover': { color: 'white' },
                  transition: 'color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                }}
                aria-label="LinkedIn"
              >
                <LinkedIn size={20} />
              </Box>
            </Box>
            */}
          </Grid>

          {/* Product Column */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography 
              variant="h6" 
              component="h4" 
              sx={{ 
                fontWeight: 600, 
                color: 'white',
                mb: 2
              }}
            >
              Product
            </Typography>
            <List sx={{ p: 0 }}>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <Link 
                  to="/#features" 
                  style={{ 
                    color: '#9ca3af',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                >
                  Features
                </Link>
              </ListItem>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <Link 
                  to="/pricing" 
                  style={{ 
                    color: '#9ca3af',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                >
                  Pricing
                </Link>
              </ListItem>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <Link 
                  to="/#faq" 
                  style={{ 
                    color: '#9ca3af',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                >
                  FAQ
                </Link>
              </ListItem>
            </List>
          </Grid>

          {/* Company Column */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography 
              variant="h6" 
              component="h4" 
              sx={{ 
                fontWeight: 600, 
                color: 'white',
                mb: 2
              }}
            >
              Company
            </Typography>
            <List sx={{ p: 0 }}>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <Link 
                  to="/about" 
                  style={{ 
                    color: '#9ca3af',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                >
                  About
                </Link>
              </ListItem>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <Link 
                  to="/contact" 
                  style={{ 
                    color: '#9ca3af',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                >
                  Contact
                </Link>
              </ListItem>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <Box
                  component="a"
                  href="#"
                  sx={{
                    color: '#9ca3af',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { color: 'white' },
                    transition: 'color 0.2s'
                  }}
                >
                  System Status
                </Box>
              </ListItem>
            </List>
          </Grid>

          {/* Legal Column */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography 
              variant="h6" 
              component="h4" 
              sx={{ 
                fontWeight: 600, 
                color: 'white',
                mb: 2
              }}
            >
              Legal
            </Typography>
            <List sx={{ p: 0 }}>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <Link 
                  to="/privacy" 
                  style={{ 
                    color: '#9ca3af',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                >
                  Privacy Policy
                </Link>
              </ListItem>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <Link 
                  to="/terms" 
                  style={{ 
                    color: '#9ca3af',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'white'}
                  onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                >
                  Terms of Service
                </Link>
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Container>

      {/* Bottom Bar */}
      <Divider sx={{ borderColor: '#374151' }} />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
            © 2026 Pelican Beaks Inc. All rights reserved.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: '#9ca3af'
            }}
          >
            <MapPin size={16} />
            <Typography variant="body2">
              625 Broad Street, Suite 240, Newark, NJ 07102
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
